import { deleteCase } from 'src/utils/api';
import { auditLogger, containsMaliciousPatterns, isValidUUID, sanitizeInput } from 'src/utils/security';
import { getCurrentUser, getSupabase } from 'src/utils/supabase';
import { normalizeText } from 'src/utils/textNormalization';

import { getCurrentUserId, verifyOwnership, withSecurity } from './secureServiceWrapper';

const normalizeSummary = (summary: unknown): string => {
  if (Array.isArray(summary)) {
    return summary
      .map((item) => {
        if (typeof item === 'string') return normalizeText(item);
        if (item && typeof item === 'object' && typeof (item as Record<string, unknown>).text === 'string') {
          const itemObj = item as Record<string, unknown>;
          let formattedText = normalizeText(itemObj.text as string);

          if (itemObj.sources && Array.isArray(itemObj.sources) && itemObj.sources.length > 0) {
            const sourceRefs = (itemObj.sources as Record<string, unknown>[])
              .map((src) => {
                const sourceName = normalizeText(src.source_name as string);
                const sourceMarker = normalizeText(src.source_marker as string);
                if (sourceName && sourceMarker) {
                  return `${sourceName} (${sourceMarker})`;
                } else if (sourceName) {
                  return sourceName;
                }
                return null;
              })
              .filter(Boolean)
              .join(', ');

            if (sourceRefs) {
              formattedText += ` [${sourceRefs}]`;
            }
          }

          return formattedText;
        }
        return '';
      })
      .filter(Boolean)
      .join('\n\n');
  }
  if (typeof summary === 'string') return normalizeText(summary);
  if (summary && typeof summary === 'object' && typeof (summary as Record<string, unknown>).text === 'string') {
    const summaryObj = summary as Record<string, unknown>;
    let formattedText = normalizeText(summaryObj.text as string);

    if (summaryObj.sources && Array.isArray(summaryObj.sources) && summaryObj.sources.length > 0) {
      const sourceRefs = (summaryObj.sources as Record<string, unknown>[])
        .map((src) => {
          const sourceName = normalizeText(src.source_name as string);
          const sourceMarker = normalizeText(src.source_marker as string);
          if (sourceName && sourceMarker) {
            return `${sourceName} (${sourceMarker})`;
          } else if (sourceName) {
            return sourceName;
          }
          return null;
        })
        .filter(Boolean)
        .join(', ');

      if (sourceRefs) {
        formattedText += ` [${sourceRefs}]`;
      }
    }

    return formattedText;
  }
  return '';
};

type LegalResearchResultRaw = LegalResearchResult & { document_ids?: string[] };

const normalizeResult = (raw: LegalResearchResultRaw | null | undefined): LegalResearchResult | undefined => {
  if (raw == null) return undefined;
  const documentIds = Array.isArray(raw.documents)
    ? raw.documents
    : Array.isArray(raw.document_ids)
      ? raw.document_ids
      : undefined;
  return {
    ...raw,
    summary: normalizeSummary(raw.summary),
    ...(documentIds !== undefined && { documents: documentIds }),
  };
};

export interface LegalResearchRequest {
  research_name: string;
  jurisdiction: string;
  legal_field: string;
  case_brief: string;
}

export interface CaseCitation {
  case_name: string;
  citation: string;
  citation_count: number;
  jurisdiction: string;
  court: string;
}

export interface KeyParagraph {
  paragraph_marker: string;
  text: string;
  importance_reason: string;
}

export interface CaseSummary {
  case_citation: CaseCitation;
  procedural_history: string;
  facts: string;
  holding: string;
  reasoning: string;
  outcome: string;
  precedent_type: 'supporting' | 'adverse';
  correlation_explanation: string;
  correlation_strength: 'high' | 'medium' | 'low';
  source_url?: string;
  new?: boolean;
  key_paragraphs?: KeyParagraph[];
}

export interface StatutorySection {
  section_number: string;
  text: string;
  interpretation_summary?: string;
}

export interface StatutoryMaterial {
  title: string;
  citation: string;
  relevant_sections: StatutorySection[];
  precedent_type: 'supporting' | 'adverse';
  correlation_explanation: string;
  correlation_strength: 'high' | 'medium' | 'low';
  source_url?: string;
}

export interface Recommendation {
  suggestion: string;
  supporting_case?: string;
}

export interface LegalResearchResult {
  type: 'legal_research';
  research_name: string;
  case_brief?: string;
  question?: string;
  answer?: string;
  jurisdiction: string;
  legal_field: string;
  summary: string;
  case_summaries: CaseSummary[];
  statutory_materials: StatutoryMaterial[];
  recommendations: Recommendation[];
  citations: string[];
  documents?: string[];
}

export interface ResearchProgress {
  phase?: string;
  progress_percent?: number;
  message?: string;
}

export interface LegalResearchDatabaseResult {
  id: string;
  entity_id: string;
  status: string;
  request: LegalResearchRequest;
  result?: LegalResearchResult;
  created_at: string;
  result_group_id: string;
  progress?: string | ResearchProgress;
}

export const legalResearchService = {
  getUserResearchResults: async (userId: string): Promise<LegalResearchDatabaseResult[]> => {
    return withSecurity(
      async () => {
        if (!isValidUUID(userId)) {
          auditLogger.log('VALIDATION_FAILURE', { reason: 'invalid_user_id', service: 'legalResearch' });
          return [];
        }

        const supabase = await getSupabase();
        const { data, error } = await supabase
          .from('legal_search_results')
          .select('*')
          .eq('entity_id', userId)
          .order('created_at', { ascending: false });

        if (error) throw error;

        return (data || []).map((row: LegalResearchDatabaseResult) => ({
          ...row,
          result: row.result ? normalizeResult(row.result) : row.result,
        }));
      },
      { requireAuth: true, rateLimitKey: 'research_get_user' }
    );
  },

  getCurrentUserResearchResults: async (): Promise<LegalResearchDatabaseResult[]> => {
    return withSecurity(
      async () => {
        const user = await getCurrentUser();
        if (!user) return [];

        const supabase = await getSupabase();
        const { data, error } = await supabase
          .from('legal_search_results')
          .select('*')
          .eq('entity_id', user.id)
          .order('created_at', { ascending: false });

        if (error) throw error;

        return (data || []).map((row: LegalResearchDatabaseResult) => ({
          ...row,
          result: row.result ? normalizeResult(row.result) : row.result,
        }));
      },
      { requireAuth: true, rateLimitKey: 'research_get_current' }
    );
  },

  getResearchResult: async (resultId: string): Promise<LegalResearchDatabaseResult | null> => {
    return withSecurity(
      async () => {
        if (!isValidUUID(resultId)) {
          auditLogger.log('VALIDATION_FAILURE', { reason: 'invalid_result_id', service: 'legalResearch' });
          return null;
        }

        await verifyOwnership('legal_search_results', resultId, 'entity_id');

        const supabase = await getSupabase();
        const { data, error } = await supabase
          .from('legal_search_results')
          .select('*')
          .eq('id', resultId)
          .maybeSingle();

        if (error) throw error;

        return data
          ? {
              ...data,
              result: data.result ? normalizeResult(data.result) : data.result,
            }
          : null;
      },
      { requireAuth: true, rateLimitKey: 'research_get_result' }
    );
  },

  getMemoBySourceResultId: async (sourceResultId: string) => {
    return withSecurity(
      async () => {
        if (!isValidUUID(sourceResultId)) {
          auditLogger.log('VALIDATION_FAILURE', { reason: 'invalid_source_result_id', service: 'legalResearch' });
          return null;
        }

        await verifyOwnership('legal_search_results', sourceResultId, 'entity_id');

        const supabase = await getSupabase();
        const { data, error } = await supabase
          .from('research_memo_results')
          .select('id, source_result_id, status, result, created_at, completed_at')
          .eq('source_result_id', sourceResultId)
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();

        if (error) throw error;
        return data;
      },
      { requireAuth: true, rateLimitKey: 'get_memo' }
    );
  },

  getMemoStatusBySourceResultIds: async (
    sourceResultIds: string[]
  ): Promise<Record<string, 'IN_PROGRESS' | 'COMPLETED' | 'FAILED'>> => {
    return withSecurity(
      async () => {
        const validIds = sourceResultIds.filter((id) => isValidUUID(id));
        if (validIds.length === 0) return {};

        const supabase = await getSupabase();
        const { data, error } = await supabase
          .from('research_memo_results')
          .select('source_result_id, status, created_at')
          .in('source_result_id', validIds)
          .order('created_at', { ascending: false });

        if (error) throw error;

        const map: Record<string, 'IN_PROGRESS' | 'COMPLETED' | 'FAILED'> = {};
        for (const row of data || []) {
          const sid = row.source_result_id as string;
          const status = (row.status as string)?.toUpperCase?.();
          if (sid && !map[sid] && (status === 'IN_PROGRESS' || status === 'COMPLETED' || status === 'FAILED')) {
            map[sid] = status as 'IN_PROGRESS' | 'COMPLETED' | 'FAILED';
          }
        }
        return map;
      },
      { requireAuth: true, rateLimitKey: 'get_memo_status_batch' }
    );
  },

  subscribeToMemoUpdates: async (sourceResultId: string, callback: (payload: unknown) => void) => {
    if (!isValidUUID(sourceResultId)) {
      auditLogger.log('VALIDATION_FAILURE', { reason: 'invalid_source_result_id', service: 'legalResearch' });
      return null;
    }

    await verifyOwnership('legal_search_results', sourceResultId, 'entity_id');

    const supabase = await getSupabase();
    const channel = supabase.channel(`memo_${sourceResultId}_${crypto.randomUUID()}`);

    channel.on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'research_memo_results',
        filter: `source_result_id=eq.${sourceResultId}`,
      },
      callback
    );

    await channel.subscribe();
    return channel;
  },

  subscribeToResearchUpdates: async (userId: string, callback: (payload: unknown) => void) => {
    if (!isValidUUID(userId)) {
      auditLogger.log('VALIDATION_FAILURE', { reason: 'invalid_user_id', service: 'research_subscribe' });
      return null;
    }

    const currentUserId = await getCurrentUserId();
    if (currentUserId !== userId) {
      auditLogger.log('OWNERSHIP_VIOLATION', { reason: 'subscribe_mismatch', service: 'research' });
      return null;
    }

    const supabase = await getSupabase();
    const channel = supabase.channel(`legal_search_results_changes_${userId}`);

    channel.on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'legal_search_results',
        filter: `entity_id=eq.${userId}`,
      },
      callback
    );

    await channel.subscribe();
    return channel;
  },

  deleteResult: async (resultId: string): Promise<void> => {
    return withSecurity(
      async () => {
        if (!isValidUUID(resultId)) {
          auditLogger.log('VALIDATION_FAILURE', { reason: 'invalid_result_id', service: 'legalResearch' });
          throw new Error('Invalid result ID');
        }

        const isOwner = await verifyOwnership('legal_search_results', resultId, 'entity_id');
        if (!isOwner) {
          auditLogger.log('OWNERSHIP_VIOLATION', { service: 'legalResearch', action: 'delete' });
          throw new Error('You do not have permission to delete this result');
        }

        const supabase = await getSupabase();

        const { data: result, error: fetchError } = await supabase
          .from('legal_search_results')
          .select('result_group_id, status')
          .eq('id', resultId)
          .maybeSingle();

        if (fetchError) {
          throw fetchError;
        }

        if (!result) {
          throw new Error('Result not found');
        }

        const { data: groupResults, error: groupError } = await supabase
          .from('legal_search_results')
          .select('id, status')
          .eq('result_group_id', result.result_group_id);

        if (groupError) {
          throw groupError;
        }

        if (!groupResults || groupResults.length === 0) {
          throw new Error('No results found in group');
        }

        const allCompletedOrFailed = groupResults.every((r) => r.status === 'COMPLETED' || r.status === 'FAILED');

        if (!allCompletedOrFailed) {
          throw new Error('Cannot delete result: All results in the result group must be COMPLETED or FAILED');
        }

        const { error: deleteError } = await supabase.from('legal_search_results').delete().eq('id', resultId);

        if (deleteError) {
          throw deleteError;
        }
      },
      { requireAuth: true, rateLimitKey: 'research_delete', rateLimitMax: 20 }
    );
  },

  removeCaseFromResearch: async (
    resultId: string,
    caseCitation: string,
    caseTitle?: string
  ): Promise<LegalResearchResult | null> => {
    return withSecurity(
      async () => {
        if (!isValidUUID(resultId)) {
          auditLogger.log('VALIDATION_FAILURE', { reason: 'invalid_result_id', service: 'legalResearch' });
          throw new Error('Invalid result ID');
        }

        const isOwner = await verifyOwnership('legal_search_results', resultId, 'entity_id');
        if (!isOwner) {
          auditLogger.log('OWNERSHIP_VIOLATION', { service: 'legalResearch', action: 'removeCase' });
          throw new Error('You do not have permission to modify this result');
        }

        const sanitizedCitation = sanitizeInput(caseCitation);
        const sanitizedTitle = caseTitle ? sanitizeInput(caseTitle) : undefined;

        if (
          containsMaliciousPatterns(sanitizedCitation) ||
          (sanitizedTitle && containsMaliciousPatterns(sanitizedTitle))
        ) {
          auditLogger.log('MALICIOUS_INPUT_BLOCKED', { service: 'legalResearch', action: 'removeCase' });
          throw new Error('Invalid input detected');
        }

        const caseName = sanitizedTitle || sanitizedCitation;

        if (!caseName) {
          throw new Error('Case name is required');
        }

        const supabase = await getSupabase();
        const { data: existing, error: fetchExistingError } = await supabase
          .from('legal_search_results')
          .select('result, request')
          .eq('id', resultId)
          .maybeSingle();

        if (fetchExistingError) {
          throw fetchExistingError;
        }

        const result = existing?.result as LegalResearchResult | undefined;
        const request = existing?.request as Record<string, unknown> | undefined;
        const question =
          result?.question ?? request?.question ?? (request?.metadata as Record<string, unknown>)?.initial_question;
        const documentIds =
          result?.documents ??
          (request && Array.isArray(request.document_ids) ? (request.document_ids as string[]) : undefined);

        const apiResponse = await deleteCase(resultId, caseName, {
          question: typeof question === 'string' ? question : undefined,
          document_ids: documentIds,
        });

        if (!apiResponse.success) {
          throw new Error(apiResponse.error || 'Failed to delete case');
        }

        const { data: updated, error: fetchError } = await supabase
          .from('legal_search_results')
          .select('result')
          .eq('id', resultId)
          .maybeSingle();

        if (fetchError) {
          throw fetchError;
        }

        if (!updated || !updated.result) {
          throw new Error('Research result not found after deletion');
        }

        return updated.result as LegalResearchResult;
      },
      { requireAuth: true, rateLimitKey: 'research_remove_case', rateLimitMax: 30 }
    );
  },

  updateCaseNewStatus: async (resultId: string, caseCitation: string): Promise<LegalResearchResult | null> => {
    return withSecurity(
      async () => {
        if (!isValidUUID(resultId)) {
          auditLogger.log('VALIDATION_FAILURE', { reason: 'invalid_result_id', service: 'legalResearch' });
          throw new Error('Invalid result ID');
        }

        const isOwner = await verifyOwnership('legal_search_results', resultId, 'entity_id');
        if (!isOwner) {
          auditLogger.log('OWNERSHIP_VIOLATION', { service: 'legalResearch', action: 'updateCaseStatus' });
          throw new Error('You do not have permission to modify this result');
        }

        const sanitizedCitation = sanitizeInput(caseCitation);
        if (containsMaliciousPatterns(sanitizedCitation)) {
          auditLogger.log('MALICIOUS_INPUT_BLOCKED', { service: 'legalResearch', action: 'updateCaseStatus' });
          throw new Error('Invalid input detected');
        }

        const supabase = await getSupabase();
        const { data: existing, error: fetchError } = await supabase
          .from('legal_search_results')
          .select('result')
          .eq('id', resultId)
          .maybeSingle();

        if (fetchError) {
          throw fetchError;
        }

        if (!existing || !existing.result) {
          throw new Error('Research result not found');
        }

        const currentResult = existing.result as LegalResearchResult;
        const normalize = (value?: string) => value?.toLowerCase?.().replace(/\s+/g, ' ').trim() || '';
        const normalizedCitation = normalize(sanitizedCitation);

        const updatedCaseSummaries = (currentResult.case_summaries || []).map((summary) => {
          const summaryCitation = normalize(summary?.case_citation?.citation);
          if (summaryCitation === normalizedCitation) {
            return {
              ...summary,
              new: false,
            };
          }
          return summary;
        });

        const updatedResult: LegalResearchResult = {
          ...currentResult,
          case_summaries: updatedCaseSummaries,
        };

        const { error: updateError } = await supabase
          .from('legal_search_results')
          .update({ result: updatedResult })
          .eq('id', resultId);

        if (updateError) {
          throw updateError;
        }

        return updatedResult;
      },
      { requireAuth: true, rateLimitKey: 'research_update_status', rateLimitMax: 50 }
    );
  },

  getSearchDomains: async (resultId: string): Promise<string[] | null> => {
    return withSecurity(
      async () => {
        if (!isValidUUID(resultId)) {
          auditLogger.log('VALIDATION_FAILURE', { reason: 'invalid_result_id', service: 'legalResearch' });
          throw new Error('Invalid result ID');
        }

        const supabase = await getSupabase();
        const { data, error } = await supabase
          .from('legal_search_results')
          .select('search_domains')
          .eq('id', resultId)
          .maybeSingle();

        if (error) {
          throw error;
        }

        return data?.search_domains || null;
      },
      { requireAuth: true, rateLimitKey: 'research_get_domains' }
    );
  },

  getClientResearchResults: async (clientId: string): Promise<LegalResearchDatabaseResult[]> => {
    return withSecurity(
      async () => {
        if (!isValidUUID(clientId)) return [];
        const supabase = await getSupabase();
        const { data, error } = await supabase
          .from('legal_search_results')
          .select('*')
          .eq('client_id', clientId)
          .order('created_at', { ascending: false });
        if (error) throw error;
        return (data || []).map((row: LegalResearchDatabaseResult) => ({
          ...row,
          result: row.result ? normalizeResult(row.result) : row.result,
        }));
      },
      { requireAuth: true, rateLimitKey: 'research_get_client' }
    );
  },

  countResearchGroups: async (): Promise<number> => {
    return withSecurity(
      async () => {
        const user = await getCurrentUser();
        if (!user) return 0;
        const supabase = await getSupabase();
        const { data, error } = await supabase
          .from('legal_search_results')
          .select('result_group_id')
          .eq('entity_id', user.id);
        if (error) return 0;
        const groupIds = new Set((data || []).map((r: { result_group_id: string }) => r.result_group_id));
        return groupIds.size;
      },
      { requireAuth: true, rateLimitKey: 'research_count' }
    );
  },
};
